import React, { useState } from "react";
import FundingMixDonut from "../components/FundingMixDonut";
import ElementsCostBars from "../components/ElementsCostBars";
import FundingLevelGauge from "../components/FundingLevelGauge";
import FundingLevelAnalysisChart from "../components/FundingLevelAnalysisChart";
import SpendingMixChart from "../components/SpendingMixChart";
import CumulativeExpensesChart from "../components/CumulativeExpensesChart";
import { ExpandablePanel, ExpandModal, PrintChartButton } from "../components/ExpandablePanel";
import { useSettings } from "../contexts/SettingsContext";

// Illustrations tab (Dave 2026-10-12) — home for illustrative charts moved
// off the Dashboard. Same 3×2 grid rhythm as the Dashboard top grid.
// 2026-10-16 (Dave) — every panel has an expand icon; Spending Mix and
// Funding Level Analysis open their dedicated full-size views, the rest
// open the same panel in a large viewer.
export default function IllustrationsScreen({ calc, association, variables, horizon }) {
  const { settings } = useSettings();
  const [expanded, setExpanded] = useState(null);
  if (!calc || !association) return null;
  const suffix = (association.tier || "pro").toLowerCase() === "basic" ? "basic" : "pro";
  const show = (name) => !settings || Number(settings[`dash_${name}_${suffix}`] ?? 1) === 1;
  const close = () => setExpanded(null);
  const common = { calc, variables, association, horizon };

  const panels = [
    { id: "funding-mix", col: 1, row: 1, flag: "funding_mix", render: () => <FundingMixDonut {...common} /> },
    { id: "elements-cost", col: 2, row: 1, flag: "elements_cost", render: () => <ElementsCostBars {...common} /> },
    { id: "funding-level", col: 3, row: 1, flag: "funding_gauge", render: () => <FundingLevelGauge {...common} /> },
    { id: "spending-mix", col: 1, row: 2, render: () => <SpendingMixChart variant="panel" {...common} /> },
    { id: "funding-analysis", col: 2, row: 2, render: () => <FundingLevelAnalysisChart variant="panel" {...common} /> },
    { id: "cumulative", col: 3, row: 2, render: () => <CumulativeExpensesChart {...common} /> },
  ];
  const active = panels.find((p) => p.id === expanded);

  return (
    <div
      className="grid grid-cols-3"
      data-testid="illustrations-screen"
      // Rows 3fr/5fr → top row is 75% of its former half-height (Dave 2026-10-12).
      style={{
        gap: "var(--u-panel-gap)",
        gridTemplateRows: "3fr 5fr",
        flex: "1 1 auto",
        minHeight: 0,
        paddingTop: "calc(var(--u-panel-gap) - 6px)", // top gap = panel gap
        paddingBottom: "calc(var(--u-panel-gap) + 8px)",
      }}
    >
      {panels.map((p) => (
        <div key={p.id} style={{ gridColumn: p.col, gridRow: p.row, minHeight: 0, display: "flex", flexDirection: "column" }}>
          {(!p.flag || show(p.flag)) && (
            <ExpandablePanel id={p.id} onExpand={setExpanded} noExpand={p.row === 1}>{p.render()}</ExpandablePanel>
          )}
        </div>
      ))}

      {expanded === "spending-mix" && (
        <SpendingMixChart open onClose={close} autoDismiss={false} {...common} />
      )}
      {expanded === "funding-analysis" && (
        <FundingLevelAnalysisChart open onClose={close} autoDismiss={false} {...common} />
      )}
      {active && !["spending-mix", "funding-analysis"].includes(active.id) && (
        <ExpandModal id={active.id} onClose={close}>{active.render()}</ExpandModal>
      )}
      {active && (
        <PrintChartButton
          targetTestId={{ "spending-mix": "spending-mix-modal", "funding-analysis": "funding-analysis-modal" }[active.id] || `illus-expanded-box-${active.id}`}
          caption={`${association.name} · ${horizon} Year Horizon · Printed ${new Date().toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })} · uReserve Planning`}
        />
      )}
    </div>
  );
}
